/**
 * Checklist tab — per-deal state for the deal's current pipeline (stage).
 *
 * Pulls items from `usePipelinePropertyChecklist`, which merges the workspace's
 * `default_checklists` template with the deal's per-item state. Check-type
 * items render as a checkbox; input-type items render as a text/number input
 * that counts as "complete" when its value is non-empty.
 *
 * Only meaningful when the property is in a pipeline. The caller should hide
 * this tab entirely for not-yet-promoted properties.
 */
import { CheckCircle2, ClipboardList } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { usePipelinePropertyChecklist } from "@/hooks/use-pipeline-property-checklist";

interface ChecklistPanelProps {
  pipelinePropertyId: string;
  pipelineId: string;
  pipelineTitle?: string;
}

export function ChecklistPanel({
  pipelinePropertyId,
  pipelineId,
  pipelineTitle,
}: ChecklistPanelProps) {
  const { items, requiredUnmet, isLoading, toggle, setInput } =
    usePipelinePropertyChecklist({ pipelinePropertyId, pipelineId });

  const completed = items.filter((i) => i.complete).length;
  const pct = items.length ? Math.round((completed / items.length) * 100) : 0;

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      {/* Stage header */}
      <div className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
        <ClipboardList
          className="h-3.5 w-3.5 text-primary"
          aria-hidden
        />
        Stage: {pipelineTitle ?? "—"}
        {items.length > 0 && (
          <span className="ml-1 text-foreground">
            · {completed} / {items.length} complete
          </span>
        )}
      </div>

      {/* Progress bar */}
      {items.length > 0 && (
        <div className="mb-3 h-1.5 overflow-hidden rounded-full border border-border bg-muted/40">
          <div
            className="h-full bg-primary transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
      )}

      {isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      )}

      {!isLoading && items.length === 0 && (
        <div className="py-10 text-center text-sm text-muted-foreground">
          <CheckCircle2
            className="mx-auto mb-2 h-8 w-8 text-muted-foreground/50"
            aria-hidden
          />
          No checklist items for this stage. Add items via Manage Columns on
          the Pipeline page.
        </div>
      )}

      {/* Items */}
      <div className="space-y-1.5">
        {items.map((item) => {
          if (item.itemType === "input") {
            return (
              <div
                key={item.key}
                className="flex items-center gap-2 rounded-md border border-border bg-muted/40 p-2.5"
              >
                <span className="flex-1 text-sm font-medium">
                  {item.label}
                  {item.required && <RequiredBadge />}
                </span>
                <Input
                  type="text"
                  defaultValue={item.stateRow?.checklist_text ?? ""}
                  onBlur={(e) =>
                    setInput.mutate({
                      defaultChecklistId: item.defaultChecklistId,
                      label: item.label,
                      value: e.target.value,
                    })
                  }
                  placeholder="—"
                  className="h-7 w-36"
                />
              </div>
            );
          }
          return (
            <div
              key={item.key}
              className="flex items-center gap-2 rounded-md border border-border bg-muted/40 p-2.5"
            >
              <button
                type="button"
                onClick={() =>
                  toggle.mutate({
                    defaultChecklistId: item.defaultChecklistId,
                    label: item.label,
                    checked: !item.complete,
                  })
                }
                className={cn(
                  "grid h-5 w-5 place-items-center rounded border-2 transition",
                  item.complete
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-card",
                )}
                aria-label={item.complete ? "Mark incomplete" : "Mark complete"}
              >
                {item.complete && (
                  <CheckCircle2 className="h-3 w-3" aria-hidden />
                )}
              </button>
              <span
                className={cn(
                  "flex-1 text-sm font-medium",
                  item.complete && "text-muted-foreground line-through",
                )}
              >
                {item.label}
                {item.required && <RequiredBadge />}
              </span>
            </div>
          );
        })}
      </div>

      {/* Required badge summary */}
      {requiredUnmet.length > 0 && (
        <div className="mt-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          {requiredUnmet.length} required item
          {requiredUnmet.length === 1 ? "" : "s"} must be completed before
          this deal can advance.
        </div>
      )}
    </div>
  );
}

function RequiredBadge() {
  return (
    <span className="ml-2 inline-block rounded-full border border-destructive/35 bg-destructive/10 px-1.5 py-0 align-middle text-[10px] font-semibold uppercase tracking-wider text-destructive">
      Required
    </span>
  );
}
